import React, { useRef } from 'react';
import { Search } from 'lucide-react';

const SearchBar = ({
  value = '',
  onChange,
  placeholder = 'Search...',
  theme = 'light',
  language = 'en',
  disabled = false,
  className = '',
  icon = true,
  iconSize = 18,
  customStyle = {},
}) => {
  const inputRef = useRef(null);
  const isRTL = language === 'ar';

  const containerStyle = {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  };

  const baseInputStyle = {
    width: '100%',
    padding: isRTL ? '12px 18px 12px 48px' : '12px 48px 12px 18px',
    paddingRight: isRTL ? '18px' : '48px',
    paddingLeft: isRTL ? '48px' : '18px',
    borderRadius: '12px',
    border: theme === 'dark'
      ? '1px solid rgba(148, 163, 184, 0.3)'
      : '1px solid rgba(148, 163, 184, 0.3)',
    background: theme === 'dark'
      ? 'rgba(15, 23, 42, 0.5)'
      : 'rgba(248, 250, 252, 0.8)',
    
    fontSize: '14px',
    fontWeight: '500',
    outline: 'none',
    transition: 'all 0.3s ease',
    textAlign: isRTL ? 'right' : 'left',
    direction: isRTL ? 'rtl' : 'ltr',
    boxSizing: 'border-box',
    cursor: disabled ? 'not-allowed' : 'text',
    opacity: disabled ? 0.6 : 1,
    ...customStyle,
  };

  const iconStyle = {
    position: 'absolute',
    left: isRTL ? '14px' : 'auto',
    right: isRTL ? 'auto' : '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    color: theme === 'dark'
      ? 'rgba(255, 255, 255, 0.5)'
      : 'rgba(51, 65, 85, 0.6)',
  };

  const handleFocus = (e) => {
    const style = e.currentTarget.style;
    style.borderColor = theme === 'dark'
      ? 'rgba(59, 130, 246, 0.5)'
      : 'rgba(59, 130, 246, 0.6)';
    style.boxShadow = theme === 'dark'
      ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
      : '0 0 0 3px rgba(59, 130, 246, 0.08)';
  };

  const handleBlur = (e) => {
    const style = e.currentTarget.style;
    style.borderColor = theme === 'dark'
      ? 'rgba(148, 163, 184, 0.3)'
      : 'rgba(148, 163, 184, 0.3)';
    style.boxShadow = 'none';
  };

  return (
    <div style={containerStyle}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`search-bar-input ${className}`}
        style={baseInputStyle}
      />
      {icon && (
        <div style={iconStyle}>
          <Search size={iconSize} />
        </div>
      )}

      <style>{`
        .search-bar-input::placeholder {
          color: ${theme === 'dark'
            ? 'rgba(203, 213, 225, 0.5)'
            : 'rgba(100, 116, 139, 0.6)'};
        }

        .search-bar-input:disabled {
          background: ${theme === 'dark'
            ? 'rgba(15, 23, 42, 0.3)'
            : 'rgba(226, 232, 240, 0.5)'};
          color: ${theme === 'dark'
            ? 'rgba(203, 213, 225, 0.5)'
            : 'rgba(100, 116, 139, 0.5)'};
          cursor: not-allowed;
        }

        .search-bar-input:hover:not(:disabled) {
          border-color: ${theme === 'dark'
            ? 'rgba(148, 163, 184, 0.5)'
            : 'rgba(148, 163, 184, 0.5)'};
          background: ${theme === 'dark'
            ? 'rgba(15, 23, 42, 0.6)'
            : 'rgba(248, 250, 252, 0.95)'};
        }

        .search-bar-input:focus {
          border-color: ${theme === 'dark'
            ? 'rgba(59, 130, 246, 0.5)'
            : 'rgba(59, 130, 246, 0.6)'};
          box-shadow: ${theme === 'dark'
            ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
            : '0 0 0 3px rgba(59, 130, 246, 0.08)'};
          background: ${theme === 'dark'
            ? 'rgba(15, 23, 42, 0.7)'
            : 'rgba(255, 255, 255, 0.95)'};
        }
      `}</style>
    </div>
  );
};

export default SearchBar;
